import { matchSorter, MatchSorterOptions } from "match-sorter";
import {
  SelectFieldOption,
  SelectFieldGroupByValue,
} from "../../beta/components/SelectField/types";

export type SyncFilterFn<T extends SelectFieldOption = SelectFieldOption> = (
  options: T[],
  searchValue: string,
) => T[];

export const defaultSyncFilter: SyncFilterFn = (options, searchValue) =>
  matchSorter(options, searchValue, {
    keys: ["label", "searchText"],
    baseSort: (a, b) => a.index - b.index,
  });

export const toSyncFilterFn = <T extends SelectFieldOption = SelectFieldOption>(
  filter: SyncFilterFn<T> | MatchSorterOptions<T>,
): SyncFilterFn<T> =>
  typeof filter === "function"
    ? filter
    : (options, searchValue) =>
        matchSorter(options, searchValue, {
          baseSort: (a, b) => a.index - b.index,
          ...filter,
        });

/**
 * Sorts options by group using the provided comparator while maintaining
 * the original order within each group (stable sort).
 */
export const sortByGroup = <T extends SelectFieldOption = SelectFieldOption>(
  options: T[],
  groupSorter: (
    a: SelectFieldGroupByValue,
    b: SelectFieldGroupByValue,
  ) => number,
): T[] => {
  const groupOrder = new Map<SelectFieldGroupByValue | undefined, number>();
  const uniqueGroups: (SelectFieldGroupByValue | undefined)[] = [];
  options.forEach((opt) => {
    if (!groupOrder.has(opt.group)) {
      groupOrder.set(opt.group, uniqueGroups.length);
      uniqueGroups.push(opt.group);
    }
  });

  uniqueGroups.sort((a, b) => {
    if (a === undefined && b === undefined) return 0;
    if (a === undefined) return 1;
    if (b === undefined) return -1;
    return groupSorter(a, b);
  });

  uniqueGroups.forEach((group, index) => {
    groupOrder.set(group, index);
  });

  return [...options].sort((a, b) => {
    const orderA = groupOrder.get(a.group) ?? Infinity;
    const orderB = groupOrder.get(b.group) ?? Infinity;
    return orderA - orderB;
  });
};
